import type { AbstractIntlMessages } from 'next-intl';

import type { AccountRegion } from '@ci/shared/currency';
import { DEFAULT_DOCKET_TERMS } from '@ci/shared/docket-terms';
import {
  DEFAULT_PAY_CYCLE_TERMS,
  type PayCycleTermKey,
} from '@ci/shared/pay-cycle-terms';
import { regionTermDefaults } from '@ci/shared/regional-terms';

/**
 * English fallback for every term in the `terms` namespace. The namespace is
 * not loaded from a JSON file: account settings override these defaults at
 * runtime (server via `mergeTerminology`, client via `TerminologyProvider`).
 *
 * Only part of this map was ever seeded into `ap_account_settings`. The
 * pay-cycle and docket terms must NEVER be seeded or backfilled: their default
 * comes from the account's region at render time (`@ci/shared/regional-terms`),
 * and a stored `fortnightly: 'Fortnightly'` or `docket: 'Docket'` would
 * override "Biweekly" or "Field Ticket" for every US and Canadian account.
 */
export const DEFAULT_TERMINOLOGY: Record<string, string> = {
  timesheet: 'Timesheet',
  material: 'Material',
  timesheets: 'Timesheets',
  order: 'Order',
  orders: 'Orders',
  project: 'Project',
  projects: 'Projects',
  client: 'Client',
  clients: 'Clients',
  invoice: 'Invoice',
  invoices: 'Invoices',
  supplier: 'Supplier',
  suppliers: 'Suppliers',
  costCode: 'Cost Code',
  costCodes: 'Cost Codes',
  quote: 'Bid',
  quotes: 'Bids',
  activity: 'Activity',
  activities: 'Activities',
  variation: 'Variation',
  variations: 'Variations',
  scheduleOfRates: 'Schedule of Rates',
  scheduleOfRatesShort: 'SoR',
  purchaseOrder: 'Purchase Order',
  purchaseOrders: 'Purchase Orders',
  form: 'Form',
  forms: 'Forms',
  inclusions: 'Inclusions',
  exclusions: 'Exclusions',
  dryHire: 'Dry Hire',
  onHire: 'On hire',
  offHire: 'Off-hire',
  hire: 'Hire',
  supervisor: 'Supervisor',
  supervisors: 'Supervisors',
  siteDiary: 'Daily Record',
  siteDiaries: 'Daily Records',
  // PAY-3455: the Schedule of Rates tab listing what an account charges.
  // Optional, so never seeded: a blank field uses this default.
  charge: 'Charge',
  charges: 'Charges',
  job: 'Job',
  jobs: 'Jobs',
  // PAY-3723: the organisation-structure levels. Never seeded into
  // `ap_account_settings`: their default follows the locale (see below).
  division: 'Division',
  divisions: 'Divisions',
  branch: 'Branch',
  branches: 'Branches',
  depot: 'Depot',
  depots: 'Depots',
  // PAY-3803: the conditional-competency statuses (a medical, for example).
  // Never seeded: they are optional terms, a blank field uses the default.
  conditionalStatusFull: 'Full',
  conditionalStatusSubjectToReview: 'Subject to review',
  conditionalStatusSubjectToConditions: 'Subject to conditions',
  conditionalStatusTemporarilyUnfit: 'Temporarily unfit',
  // PAY-5180: the Safeworking nouns. Optional and locale-aware like the
  // organisation-level terms, so never seeded.
  competency: 'Competency',
  competencies: 'Competencies',
  role: 'Role',
  roles: 'Roles',
  ...DEFAULT_PAY_CYCLE_TERMS,
  // PAY-4262: docket, dockets, docketed. Their default follows the region
  // (Field Ticket in the US and Canada), so they are never seeded either.
  ...DEFAULT_DOCKET_TERMS,
  // PAY-4613: HSEQ issue types
  hseqObservation: 'Observation',
  hseqObservations: 'Observations',
  hseqNcr: 'NCR',
  hseqNcrs: 'NCRs',
  hseqPunchlistItem: 'Punch List Item',
  hseqPunchlistItems: 'Punch List Items',
  hseqIncident: 'Incident',
  hseqIncidents: 'Incidents',
  // PAY-3597: optional, off until the account enables the type.
  hseqPersonnelNc: 'Personnel non-conformance',
  hseqPersonnelNcs: 'Personnel non-conformances',
  // PAY-4931: punchlist walks. Optional, like the issue-type terms above.
  hseqWalk: 'Punchlist walk',
  hseqWalks: 'Punchlist walks',
};

/**
 * The per-locale replacements for {@link DEFAULT_TERMINOLOGY} entries. English
 * (and en-AU) has none: {@link DEFAULT_TERMINOLOGY} IS the English default.
 *
 * Only the organisation-level terms (PAY-3723), the conditional-status terms
 * (PAY-3803), the competency and role terms (PAY-5180), and the HSEQ terms
 * (PAY-4613, PAY-4931) are localised. Every
 * older term is English in every locale by design (the account's rename is
 * the source of truth, and existing accounts must not see their defaults
 * change), so do not add to this map without deciding that for the term. An
 * account's own override is one value for all locales and always wins over
 * these.
 */
const LOCALE_TERMINOLOGY_DEFAULTS: Record<string, Record<string, string>> = {
  es: {
    division: 'División',
    divisions: 'Divisiones',
    branch: 'Sucursal',
    branches: 'Sucursales',
    depot: 'Depósito',
    depots: 'Depósitos',
    conditionalStatusFull: 'Completo',
    conditionalStatusSubjectToReview: 'Sujeto a revisión',
    conditionalStatusSubjectToConditions: 'Sujeto a condiciones',
    conditionalStatusTemporarilyUnfit: 'Temporalmente no apto',
    competency: 'Competencia',
    competencies: 'Competencias',
    role: 'Rol',
    roles: 'Roles',
    hseqObservation: 'Observación',
    hseqObservations: 'Observaciones',
    hseqNcr: 'RNC',
    hseqNcrs: 'RNCs',
    hseqPunchlistItem: 'Elemento de lista de pendientes',
    hseqPunchlistItems: 'Elementos de lista de pendientes',
    hseqIncident: 'Incidente',
    hseqIncidents: 'Incidentes',
    hseqPersonnelNc: 'No conformidad de personal',
    hseqPersonnelNcs: 'No conformidades de personal',
    hseqWalk: 'Recorrido de pendientes',
    hseqWalks: 'Recorridos de pendientes',
  },
};

/** The terms whose default differs from English in `locale`; none for en. */
function localeTerminologyOverrides(
  locale: string | null | undefined,
): Record<string, string> {
  return locale && Object.hasOwn(LOCALE_TERMINOLOGY_DEFAULTS, locale)
    ? LOCALE_TERMINOLOGY_DEFAULTS[locale]!
    : {};
}

const LOCALIZED_DEFAULTS: Record<
  string,
  Record<string, string>
> = Object.fromEntries(
  Object.entries(LOCALE_TERMINOLOGY_DEFAULTS).map(([locale, overrides]) => [
    locale,
    { ...DEFAULT_TERMINOLOGY, ...overrides },
  ]),
);

/**
 * The default `terms` for a locale: {@link DEFAULT_TERMINOLOGY} with that
 * locale's overrides on top, and English for a locale that has none (or none
 * given). A stable reference per locale.
 */
export function defaultTerminologyFor(
  locale?: string | null,
): Record<string, string> {
  return locale && Object.hasOwn(LOCALIZED_DEFAULTS, locale)
    ? LOCALIZED_DEFAULTS[locale]!
    : DEFAULT_TERMINOLOGY;
}

/** How a resolved term is cased at the point it is rendered. */
export type TermCase = 'upper' | 'lower';

/**
 * Case a resolved term for the sentence it lands in.
 *
 * Terminology values are stored in their canonical case ("Dry Hire",
 * "Off-hire"), which is right for a label and wrong for a chip (DRY HIRE) or
 * for the middle of a sentence ("no off-hire date has been nominated"). This
 * is the ONE place that decides, and it has two callers: the `|upper` /
 * `|lower` modifier on a `$t(terms:…)` token, and components rendering a term
 * they read from the message tree.
 *
 * A string helper rather than an `uppercase` Tailwind class because the same
 * label goes into a `title` attribute and into e2e assertions, where CSS
 * casing is invisible. Pinned to `en` so a Turkish browser locale cannot dot
 * the I.
 */
export function termCase(value: string, mode: TermCase): string {
  return mode === 'upper'
    ? value.toLocaleUpperCase('en')
    : value.toLocaleLowerCase('en');
}

/**
 * The word "fortnight" (and "fortnightly", "fortnights") inside ENGINE text,
 * but not inside an identifier: `fortnightlyOvertime` and `fortnight_id` have
 * no word boundary, and the hyphen look-arounds keep ids such as
 * `fortnightly-threshold-<uuid>` intact. The same look-arounds also leave
 * hyphenated prose ("mid-fortnight") untouched; the engine writes none today.
 */
const PAY_CYCLE_WORD_PATTERN = /(?<!-)\bfortnight(ly|s)?\b(?!-)/gi;

/**
 * Whether {@link localizePayCycleWords} could change this text for SOME
 * account. Lets a caller that would have to read the account's terms first
 * skip the read for text that names no pay cycle (PAY-3382).
 */
export function containsPayCycleWord(text: string): boolean {
  // `search` ignores the pattern's `g` flag and its `lastIndex`, so the shared
  // global regex carries no state between calls.
  return text.search(PAY_CYCLE_WORD_PATTERN) !== -1;
}

/**
 * Put the account's pay-cycle terms into text the payroll engine wrote
 * (PAY-3372).
 *
 * The engine runs in trigger.dev with no account terminology, and what it
 * writes into `calculated_pay_rates` (the Milo reasoning, the calculation
 * warning, a line's applied conditions) is English that says "Fortnightly
 * Overtime", "the fortnight starting 2026-01-05" and so on. It stays that way
 * in storage on purpose: line names are part of the engine's consolidation
 * key, and the public API returns them. So the words are swapped where the
 * text is DISPLAYED, not where it is written.
 *
 * Case follows the word being replaced: "Fortnightly" at the start of a
 * sentence becomes "Biweekly", "fortnight" mid-sentence becomes
 * "two-week period", "FORTNIGHTLY" is upper-cased. A lowercase match
 * lower-cases the whole term, the same rule as `$t(terms:…|lower)`. The plural
 * "fortnights" appends `s` to the account's noun. A no-op when both terms are
 * the stock ones, so an AU account's text is returned untouched.
 *
 * Known limit: engine text quotes some user-entered values (a rule's name, a
 * cost code). A US account whose own rule is literally named "Fortnightly OT"
 * will see "Biweekly OT" in the reasoning. That account has chosen not to use
 * the word, so this errs the right way.
 *
 * @param text - engine-written text, as stored
 * @param terms - the resolved `terms` namespace (account over region over
 *   default)
 */
export function localizePayCycleWords(
  text: string,
  terms: Partial<Record<PayCycleTermKey, string>>,
): string {
  const fortnight =
    terms.fortnight?.trim() || DEFAULT_PAY_CYCLE_TERMS.fortnight;
  const fortnightly =
    terms.fortnightly?.trim() || DEFAULT_PAY_CYCLE_TERMS.fortnightly;

  if (
    fortnight === DEFAULT_PAY_CYCLE_TERMS.fortnight &&
    fortnightly === DEFAULT_PAY_CYCLE_TERMS.fortnightly
  ) {
    return text;
  }

  return text.replace(
    PAY_CYCLE_WORD_PATTERN,
    (match: string, suffix: string | undefined) => {
      const kind = suffix?.toLowerCase();
      const term =
        kind === 'ly'
          ? fortnightly
          : kind === 's'
            ? `${fortnight}s`
            : fortnight;

      if (match.length > 1 && match === termCase(match, 'upper')) {
        return termCase(term, 'upper');
      }

      const first = match.charAt(0);
      if (first === termCase(first, 'lower')) {
        return termCase(term, 'lower');
      }

      return termCase(term.charAt(0), 'upper') + term.slice(1);
    },
  );
}

const TERMS_PREFIX = 'terms.';

/**
 * i18next cross-namespace references left in JSON message strings, with an
 * optional case modifier: `$t(terms:dryHire)` renders the term as stored,
 * `$t(terms:dryHire|upper)` for a chip, `$t(terms:offHire|lower)` for the
 * middle of a sentence. Same token, same resolver — the modifier exists so a
 * renameable noun can appear in prose without capitalising it mid-sentence.
 */
const TERM_REFERENCE_PATTERN =
  /\$t\(terms:([a-zA-Z][a-zA-Z0-9]*)(?:\|(upper|lower))?\)/g;

/**
 * Replace legacy `$t(terms:key)` references in a single string with the
 * resolved term from the given `terms` map. Used for runtime values (e.g.
 * DB-stored comments) that carry the legacy token but never pass through the
 * message-tree resolution below. next-intl does not interpret i18next-style
 * cross-references at format time, so the substitution must be explicit.
 */
export function resolveTermReferencesInString(
  value: string,
  terms: Record<string, string>,
): string {
  return value.replace(
    TERM_REFERENCE_PATTERN,
    (_match, key: string, mode: TermCase | undefined) => {
      const term = terms[key] ?? key;

      return mode ? termCase(term, mode) : term;
    },
  );
}

/**
 * Replace legacy `$t(terms:key)` references in message strings with the
 * resolved term from the `terms` namespace. next-intl does not interpret
 * i18next-style cross-references at format time.
 */
export function resolveTermReferences(
  messages: AbstractIntlMessages,
): AbstractIntlMessages {
  const terms = (messages.terms as Record<string, string> | undefined) ?? {};

  function walk(node: unknown): unknown {
    if (typeof node === 'string') {
      return resolveTermReferencesInString(node, terms);
    }

    if (Array.isArray(node)) {
      return node.map(walk);
    }

    if (node && typeof node === 'object') {
      return Object.fromEntries(
        Object.entries(node).map(([key, value]) => [key, walk(value)]),
      );
    }

    return node;
  }

  return walk(messages) as AbstractIntlMessages;
}

/**
 * One message namespace with its `$t(terms:…)` references resolved to the
 * locale's default terms (English when no locale is given). For contexts that
 * load a namespace directly instead of through the request messages, such as
 * component tests.
 */
export function withDefaultTerms<T extends AbstractIntlMessages>(
  ns: T,
  locale?: string,
): T {
  return resolveTermReferences({ ns, terms: defaultTerminologyFor(locale) })
    .ns as T;
}

/**
 * Normalize terminology keys from legacy i18next-style storage to the flat
 * keys next-intl expects under `messages.terms`.
 *
 * Handles both `terms:scheduleOfRates` (i18next namespace:key) and
 * `terms.scheduleOfRates` (dot path) without requiring a DB migration.
 *
 * Also DROPS any value that is not a non-blank string, the same rule
 * {@link resolveTerm} applies. The setting is user-writable, and a stored blank
 * must mean "use the default", not render an empty label or pin the region's
 * pay-cycle wording away.
 */
export function normalizeTerminologyKeys(
  terminology: Record<string, unknown>,
): Record<string, string> {
  const normalized: Record<string, string> = {};

  for (const [key, value] of Object.entries(terminology)) {
    if (typeof value !== 'string' || !value.trim()) continue;

    let normalizedKey = key;

    if (normalizedKey.startsWith('terms:')) {
      normalizedKey = normalizedKey.slice('terms:'.length);
    } else if (normalizedKey.startsWith(TERMS_PREFIX)) {
      normalizedKey = normalizedKey.slice(TERMS_PREFIX.length);
    }

    normalized[normalizedKey] = value;
  }

  return normalized;
}

/**
 * Parse the raw `ap_account_settings.setting_value` of the `terminology`
 * setting into a terms map. Accepts the jsonb object PostgREST returns and,
 * defensively, a JSON string. Anything else (null, arrays, malformed JSON)
 * resolves to `null` so callers fall back to `DEFAULT_TERMINOLOGY`. Keys are
 * returned as stored; `mergeTerminology` normalises them.
 */
export function parseTerminologySetting(
  raw: unknown,
): Record<string, string> | null {
  let value = raw;

  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, string>;
  }

  return null;
}

/**
 * Resolve ONE account term outside the next-intl message tree — the invoice /
 * quote PDF renders and the trigger.dev jobs that share them, where there is
 * no request to read a translator from.
 *
 * `raw` is the `terminology` setting exactly as stored: jsonb from PostgREST,
 * defensively a JSON string, or junk. Every VALUE is guarded, not just the
 * container: the setting is user-writable, so a key can legitimately hold a
 * number, `null` or a blank string, and handing that to a caller that expects
 * a string (`term.trim()`) throws. Anything that is not a non-blank string
 * falls back to {@link defaultTerminologyFor} the `locale` (English when none
 * is given), and a key with no default at all resolves to the key itself.
 *
 * Accepts the same legacy key spellings as {@link normalizeTerminologyKeys}
 * (`timesheets`, `terms:timesheets`, `terms.timesheets`).
 */
export function resolveTerm(
  raw: unknown,
  key: string,
  locale?: string | null,
): string {
  const fallback = defaultTerminologyFor(locale)[key] ?? key;
  const parsed = parseTerminologySetting(raw) as Record<string, unknown> | null;

  if (!parsed) {
    return fallback;
  }

  for (const candidate of [key, `terms:${key}`, `${TERMS_PREFIX}${key}`]) {
    const value = parsed[candidate];

    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return fallback;
}

/**
 * Merge an account's custom terminology into the next-intl `terms` namespace.
 *
 * next-intl messages are immutable (unlike react-i18next's `addResourceBundle`),
 * so instead of mutating a shared server instance we return a NEW messages
 * object with the account terminology shallow-merged over the default `terms`.
 *
 * @param messages - the base messages loaded for the request locale
 * @param terminology - the account's `terminology` setting (or null)
 * @param region - the account's PARSED region. It decides the DEFAULT for the
 *   pay-cycle and docket terms only; the account's own terminology still
 *   wins. Required so a new caller has to decide: omitting it would silently
 *   give US and Canadian accounts the AU wording. Pass `null` only where no
 *   pay-cycle or docket term can render.
 * @param locale - the request locale. It decides the DEFAULT of the
 *   organisation-level terms only (Spanish for `es`); omitted means English.
 *   The account's own terminology still wins.
 */
export function mergeTerminology(
  messages: AbstractIntlMessages,
  terminology: Record<string, string> | undefined | null,
  region: AccountRegion | null,
  locale?: string | null,
): AbstractIntlMessages {
  // Only the pay-cycle terms (PAY-3372) and the docket terms (PAY-4262) vary
  // by region, and only the organisation-level terms by locale (PAY-3723);
  // every other term has one default.
  const overlay = {
    ...localeTerminologyOverrides(locale),
    ...regionTermDefaults(region),
    ...(terminology && typeof terminology === 'object'
      ? normalizeTerminologyKeys(terminology)
      : {}),
  };

  const messagesWithTerms = Object.keys(overlay).length
    ? {
        ...messages,
        terms: {
          ...((messages.terms as AbstractIntlMessages | undefined) ?? {}),
          ...overlay,
        },
      }
    : messages;

  return resolveTermReferences(messagesWithTerms);
}

export const PERSONAL_HOME_SEGMENTS: ReadonlySet<string> = new Set([
  'billing',
  'reviews',
  'settings',
  'shared-dashboards',
  'supplier',
]);

/**
 * Extract the team account slug from a request pathname, if present.
 *
 * Handles both locale-prefixed (`/en/home/acme/...`) and bare
 * (`/home/acme/...`) paths by locating the `home` segment and taking the
 * segment after it. Returns null for non-team routes (marketing, auth, and the
 * personal account's own pages under `/home`).
 */
export function getAccountSlugFromPathname(
  pathname: string | null | undefined,
): string | null {
  if (!pathname) return null;

  const segments = pathname.split('/').filter(Boolean);
  const homeIndex = segments.indexOf('home');

  if (homeIndex === -1) return null;

  const slug = segments[homeIndex + 1];

  if (!slug || PERSONAL_HOME_SEGMENTS.has(slug)) return null;

  return slug;
}
